import { GC_TIME_LONG, MINIMUM_VIABLE_PLUME_TOKEN_AMOUNT, QueryKey, STALE_TIME_SHORT } from '@/constants/shared';
import { makeRequest } from '@/network/api';
import { useQuery } from '@tanstack/react-query';
import { Address } from 'viem';

interface TokenBalanceInfo {
  token: {
    address: Address;
    name: string;
    symbol: string;
    imageLargeUrl: string;
    imageThumbUrl: string;
    imageSmallUrl: string;
    priceUSD: number;
    change24: string | null;
    change24Float: number | null;
    tokenType: string;
    tokenId: number;
    protocol: string;
    protocolIcon: string;
    decimals: number;
  };
  holdings: {
    tokenBalance: number;
    valueUSD: number;
  };
}

// set return type
interface WalletBalanceInfo {
  totalUSDValueOfWallet: number;
  walletTokenBalanceInfoArr: TokenBalanceInfo[];
  hasInsignificantBalance: boolean;
  isLoading: boolean;
  refetchBalance: () => void;
}

export const useWalletBalanceInfo = (walletAddress: string | undefined): WalletBalanceInfo => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [QueryKey.TokenDetails, walletAddress],
    queryFn: async () => {
      if (!walletAddress) return {};

      const response = await makeRequest(`wallet-balance?walletAddress=${walletAddress}`);
      if (!response.ok) {
        throw new Error('Failed to fetch wallet balance');
      }

      const responseData = await response.json();
      return responseData;
    },
    enabled: Boolean(walletAddress),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME_SHORT,
    gcTime: GC_TIME_LONG,
  });

  const {
    totalUSDValueOfWallet = 0,
    walletTokenBalanceInfoArr = [],
    hasInsignificantBalance = totalUSDValueOfWallet < MINIMUM_VIABLE_PLUME_TOKEN_AMOUNT,
  } = data ?? {};

  const isWalletLoading = isLoading || !walletAddress;

  return {
    isLoading: isWalletLoading,
    totalUSDValueOfWallet,
    walletTokenBalanceInfoArr,
    hasInsignificantBalance,
    refetchBalance: refetch,
  };
};
